import { cleanEmpty } from './utils.js'

/**
 * Utility functions for detecting GeoTIFF files by checking for specific byte patterns
 */

/**
 * Check if data is a Buffer
 */
function isBuffer(data: unknown): boolean {
  return typeof Buffer !== 'undefined' && Buffer.isBuffer(data)
}

/**
 * Get length of array-like object
 */
function getLength(arr: ArrayBuffer | Uint8Array | Buffer): number {
  if ('length' in arr) return (arr as unknown as ArrayLike<unknown>).length
  if ('byteLength' in arr) return (arr as ArrayBuffer).byteLength
  return 0
}

interface ByteSearchOptions {
  data: Uint8Array | ArrayBuffer | Buffer
  sequences: Record<string, number[]>
  start?: number
  end?: number
  threshold?: number
}

interface ByteSearchResult {
  found: number
  result: boolean
  threshold: number
  tree?: Record<string, unknown>
}

const DEFAULT_GEOTIFF_THRESHOLD = 3
const COG_THRESHOLD = 4

/**
 * Check if data contains specific byte sequences
 */
function findByteSequences({
  data,
  sequences,
  start = 0,
  end,
  threshold,
}: ByteSearchOptions): ByteSearchResult {
  if (!data) throw new Error('You must specify data')

  // Validate data type
  const valid =
    data instanceof ArrayBuffer || data instanceof Uint8Array || isBuffer(data)

  if (!valid) {
    throw new Error('Data must be an ArrayBuffer, Buffer, or Uint8Array')
  }

  // Calculate positions
  const startPos = start
  const endPos = end ?? getLength(data) - 1
  const thresholdCount = threshold ?? Object.keys(sequences).length

  // Convert to Uint8Array for consistent access
  let dataArray: Uint8Array

  if (data instanceof Uint8Array) {
    dataArray = data
  } else {
    dataArray = new Uint8Array(
      data instanceof ArrayBuffer ? data : (data as Buffer).buffer,
      startPos,
      endPos - startPos + 1,
    )
  }

  // Create indexed search tree
  const tree: Record<string, unknown> = {}

  for (const name in sequences) {
    const sequence = sequences[name]
    let branch = tree

    for (let i = 0; i < sequence.length - 1; i++) {
      const byte = sequence[i]
      if (!branch[byte]) branch[byte] = {}
      branch = branch[byte] as Record<string, unknown>
    }

    branch[sequence[sequence.length - 1]] = name
  }

  // Search for matches
  const found = new Set<string>()
  for (let i = 0; i <= endPos - startPos; i++) {
    let branch = tree

    for (let j = i; j <= endPos - startPos; j++) {
      const next = dataArray[j]
      const type = typeof branch[next]

      if (type === 'undefined') {
        break
      } else if (type === 'object') {
        branch = branch[next] as Record<string, unknown>
      } else if (type === 'string') {
        const match = branch[next] as string
        found.add(match)
        delete branch[next] // Prevent duplicate matches

        if (found.size === thresholdCount) {
          return {
            found: thresholdCount,
            result: true,
            threshold: thresholdCount,
          }
        }
      }
    }
  }

  return {
    found: found.size,
    result: found.size >= thresholdCount,
    threshold: thresholdCount,
  }
}

/**
 * GeoTIFF tag byte sequences to look for
 */
const GEOTIFF_TAGS: Record<string, number[]> = {
  GeoKeyDirectoryTag: [135, 175], // 0x87AF
  ModelPixelScaleTag: [14, 131], // 0x830E
  ModelTiepointTag: [130, 132], // 0x8482
  GeoDoubleParamsTag: [176, 135], // 0x87B0
  GeoAsciiParamsTag: [177, 135], // 0x87B1
  GDAL_NODATA: [129, 164], // 0xA481
}

interface IsGeoTiffOptions {
  data: ArrayBuffer | Uint8Array | Buffer
  read_length?: number
  threshold?: number
}

/**
 * Check if data is a GeoTIFF file by looking for GeoTIFF tags
 */
export function isGeoTiff({
  data,
  read_length = 1000,
  threshold = DEFAULT_GEOTIFF_THRESHOLD,
}: IsGeoTiffOptions): ByteSearchResult {
  const result = findByteSequences({
    data,
    end: read_length - 1,
    sequences: GEOTIFF_TAGS,
    threshold,
  })
  return result
}

/**
 * COG tag byte sequences to look for
 */
const COG_TAGS: Record<string, number[]> = {
  TILE_WIDTH: [66, 1], // 0x0142
  TILE_HEIGHT: [67, 1], // 0x0143
  TILE_OFFSETS: [68, 1], // 0x0144
  TILE_BYTE_COUNTS: [69, 1], // 0x0145
}

/**
 * Options for checking if data is a COG file
 */
interface IsCOGOptions {
  data: ArrayBuffer | Uint8Array | Buffer
  read_length?: number
  geotiff_threshold?: number
}

/**
 * Check if data is a Cloud Optimized GeoTIFF (COG) file by looking for COG tags
 */
export function isCOG({
  data,
  read_length = 1000,
  geotiff_threshold = DEFAULT_GEOTIFF_THRESHOLD,
}: IsCOGOptions): ByteSearchResult {
  const geoTiffResult = isGeoTiff({
    data,
    read_length,
    threshold: geotiff_threshold,
  })

  if (!geoTiffResult.result) {
    return {
      found: 0,
      result: false,
      threshold: geotiff_threshold,
    }
  }

  const cogResult = findByteSequences({
    data,
    end: read_length - 1,
    sequences: COG_TAGS,
    threshold: COG_THRESHOLD,
  })
  return cogResult
}

/**
 * Get the type of TIFF file
 */
export function getTiffType(data: ArrayBuffer | Uint8Array | Buffer): string {
  const cogResult = isCOG({
    data,
    read_length: 1000,
  })
  if (cogResult.result) {
    return 'cog'
  }

  const geoTiffResult = isGeoTiff({
    data,
    read_length: 1000,
    threshold: DEFAULT_GEOTIFF_THRESHOLD,
  })
  if (geoTiffResult.result) {
    return 'geotiff'
  }

  return 'tiff'
}

/**
 * Position metadata extracted from image EXIF
 */
export type PositionMetadata = {
  latitude: number
  longitude: number
  absoluteAltitude?: number
  relativeAltitude?: number
}

/**
 * Camera metadata extracted from EXIF
 */
export type CameraMetadata = {
  make?: string
  model?: string
  focalLength?: number
  focalLengthIn35mm?: number
  calibratedFocalLength?: number
  exifImageWidth?: number
  exifImageHeight?: number
}

/**
 * Drone metadata extracted from EXIF
 */
export type DroneMetadata = {
  gimbalRollDegree?: number
  gimbalYawDegree?: number
  gimbalPitchDegree?: number
  flightRollDegree?: number
  flightYawDegree?: number
  flightPitchDegree?: number
  flightXSpeed?: number
  flightYSpeed?: number
  flightZSpeed?: number
}

/**
 * How an image's capture instant was derived — the provenance of
 * {@link ExifMetadata.timestamp}.
 *
 * EXIF timestamps are the camera's local wall clock with no timezone
 * attached, so turning one into an instant always involves finding an offset
 * from somewhere. This records where it came from, which is what lets a
 * reader tell a real instant from a wall clock we could not anchor.
 *
 * An **absent** source means the value predates droidmap#1988 and carries the
 * legacy convention: the camera's wall clock stamped with a `Z` it never
 * earned. Treat those the way `captureWallClock` does, not as instants.
 */
export type CaptureTimestampSource =
  /** An EXIF `Offset*` tag stated the camera's UTC offset outright. */
  | 'exif-offset'
  /**
   * The GPS receiver's own UTC clock, which is the instant by construction.
   * The camera's clock is compared against it only to recover the offset for
   * display, and a comparison that does not land on a real offset rejects the
   * anchor rather than bending it.
   */
  | 'gps'
  /**
   * Offset *inferred* from the EXIF GPS position: the coordinates resolve to an
   * IANA zone, and the camera's wall clock is read in that zone (droidmap#2783).
   *
   * Weaker than the two above — those observe an offset, this deduces one — but
   * it is the only anchor most of the fleet offers, since the common DJI models
   * write a position and no clock at all. A wrong answer is almost always the
   * neighbouring zone, so the error is bounded near an hour, against the camera's
   * whole offset (up to 14h) if the capture is left unanchored.
   */
  | 'geo'
  /** Neither was available; the wall clock is stamped as UTC and is not a true instant. */
  | 'local-assumed'
  /** Set by a user, overriding whatever extraction produced. Never overwritten. */
  | 'manual'

/**
 * Complete metadata extracted from image EXIF
 */
export type ExifMetadata = {
  position?: PositionMetadata
  /**
   * The capture time as an ISO-8601 UTC instant — a *true* instant unless
   * {@link ExifMetadata.timestampSource} is `'local-assumed'`.
   */
  timestamp?: string
  /** How {@link ExifMetadata.timestamp} was derived. */
  timestampSource?: CaptureTimestampSource
  /**
   * The camera's UTC offset in minutes (east positive), when one was found.
   * Retained so a display can render the photographer's own wall clock — the
   * flight time they'd recognise — without re-deriving it. Absent for
   * `'local-assumed'`; legitimately `0` for a camera genuinely on UTC.
   */
  timestampOffsetMinutes?: number
  camera?: CameraMetadata
  drone?: DroneMetadata
}

/* --------------------------- Capture time (#1988) -------------------------- */

/**
 * The camera's UTC offset in minutes (east positive) recorded against a
 * capture, or `undefined` when one was never determined.
 *
 * Reads `timestampOffsetMinutes` off the metadata blob rather than the promoted
 * `captured_at_offset_minutes` column: the column is written only by the
 * capture backfill, while this field is populated for backfilled rows *and*
 * everything ingested since, which makes it the one that is actually there.
 * Lidar metadata carries no offset, so the union is narrowed rather than cast.
 *
 * `0` is a real answer — a camera genuinely set to UTC — so it must survive a
 * caller's `??`; only a missing offset comes back `undefined`.
 *
 * @example
 * // Pair with the offset's instant to render the photographer's own clock:
 * const shown = captureDisplayDate(asset.capturedAt, captureOffsetMinutes(asset.assetMetadata))
 */
export const captureOffsetMinutes = (
  metadata?: { timestampOffsetMinutes?: number } | { timestamp?: string },
): number | undefined => {
  if (!metadata || !('timestampOffsetMinutes' in metadata)) return undefined
  const offset = metadata.timestampOffsetMinutes
  return typeof offset === 'number' ? offset : undefined
}

/** Real UTC offsets span -12:00 (Baker Island) to +14:00 (Line Islands). */
const MIN_UTC_OFFSET_MINUTES = -12 * 60
const MAX_UTC_OFFSET_MINUTES = 14 * 60

/**
 * Every real UTC offset is a whole number of quarter hours (+05:45 Nepal and
 * +08:45 Eucla being the awkward ones). A GPS-derived offset also carries the
 * drift between the camera's clock and the GPS receiver's — seconds, sometimes
 * a minute — so it has to be snapped to that grid, or every image in a flight
 * ends up with its own bespoke offset.
 */
const OFFSET_QUANTUM_MINUTES = 15

const isPlausibleUtcOffset = (minutes: number): boolean =>
  Number.isFinite(minutes) &&
  minutes >= MIN_UTC_OFFSET_MINUTES &&
  minutes <= MAX_UTC_OFFSET_MINUTES

/**
 * The sub-hour offsets that any inhabited place has actually kept, in minutes
 * east. Every other real offset is a whole hour.
 *
 * Being on the quarter-hour grid is necessary but not sufficient: `+10:15` is
 * a multiple of {@link OFFSET_QUANTUM_MINUTES} inside the plausible range and
 * has never been a timezone anywhere. Snapping only to the grid therefore
 * turns a camera more than half a quantum out of step with its GPS receiver
 * into a confident, invented offset (droidmap#2783 review) — so the snap is
 * checked for membership here as well as for range.
 *
 * Includes the recently-retired ones, because a photo carries the rules of the
 * day it was taken: Venezuela's -04:30 ran to 2016, North Korea's +08:30 to
 * 2018, Norfolk Island's +11:30 to 2015.
 */
const REAL_SUB_HOUR_OFFSET_MINUTES: ReadonlySet<number> = new Set([
  -9 * 60 - 30, // Marquesas
  -4 * 60 - 30, // Venezuela, until 2016
  -3 * 60 - 30, // Newfoundland
  -2 * 60 - 30, // Newfoundland, DST
  3 * 60 + 30, // Iran
  4 * 60 + 30, // Afghanistan
  5 * 60 + 30, // India, Sri Lanka
  5 * 60 + 45, // Nepal
  6 * 60 + 30, // Myanmar, Cocos Islands
  8 * 60 + 30, // North Korea, until 2018
  8 * 60 + 45, // Eucla
  9 * 60 + 30, // Australian Central
  10 * 60 + 30, // Lord Howe Island, DST
  11 * 60 + 30, // Norfolk Island, until 2015
  12 * 60 + 45, // Chatham Islands
  13 * 60 + 45, // Chatham Islands, DST
])

/**
 * Whether an offset is one somewhere has genuinely kept, rather than merely a
 * number inside the plausible range.
 *
 * Applied to the *derived* offset only — the one this module computes from two
 * clocks and rounds. An `OffsetTime*` tag is the camera asserting its own
 * offset and a zone offset comes from ICU, so neither is this module's
 * arithmetic to second-guess; both keep the range check alone.
 */
const isRealUtcOffset = (minutes: number): boolean =>
  isPlausibleUtcOffset(minutes) &&
  (minutes % 60 === 0 || REAL_SUB_HOUR_OFFSET_MINUTES.has(minutes))

/** The camera's clock face: the digits EXIF stores, with no timezone applied. */
interface WallClock {
  year: number
  /** 1-12, as written in EXIF — not the 0-11 a `Date` constructor wants. */
  month: number
  day: number
  hours: number
  minutes: number
  seconds: number
}

/** Length of each month in a non-leap year, January first. */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const

/**
 * Days in a 1-12 month of a proleptic-Gregorian year. A few lines of
 * arithmetic rather than a date library: this module is bundled into the
 * browser upload path and carries no dependencies.
 */
const daysInMonth = (year: number, month: number): number => {
  if (month !== 2) return DAYS_IN_MONTH[month - 1]
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  return leap ? 29 : 28
}

/**
 * Read an EXIF date tag as the photographer would have read the camera's
 * screen — no timezone applied, no instant implied.
 *
 * exifr revives EXIF date tags through `new Date(y, m - 1, d)` + `setHours(…)`,
 * i.e. via the *parsing process's* local timezone. Reading the components back
 * with the matching local getters therefore returns the original EXIF digits
 * whatever timezone that process runs in. `toISOString()` — what this replaced
 * — instead bakes the parsing process's offset into the result, which is the
 * bug in droidmap#1988: correct only because ingestion happens to run in UTC.
 *
 * One residual sharp edge: a wall clock landing inside a DST spring-forward
 * gap *in the parsing process's zone* is normalised away by the `Date`
 * constructor before we ever see it. UTC has no such gap, and both ingestion
 * paths run in UTC.
 */
const readWallClock = (value: unknown): WallClock | undefined => {
  let parts: WallClock | undefined

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined
    parts = {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hours: value.getHours(),
      minutes: value.getMinutes(),
      seconds: value.getSeconds(),
    }
  } else if (typeof value === 'string') {
    // exifr hands back the raw string when it cannot revive the tag. EXIF
    // writes "YYYY:MM:DD HH:MM:SS"; accept the ISO-ish spellings too.
    const match = value
      .replace(/\0/g, '')
      .trim()
      .match(/^(\d{4})[-:](\d{2})[-:](\d{2})[T ](\d{2}):(\d{2}):(\d{2})/)
    if (!match) return undefined
    parts = {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hours: Number(match[4]),
      minutes: Number(match[5]),
      seconds: Number(match[6]),
    }
  }

  if (!parts) return undefined

  // Cameras with no clock set write "0000:00:00 00:00:00". That is not a date,
  // and stamping it as one puts assets at the Unix epoch's neighbourhood where
  // they sort ahead of everything real.
  //
  // The day is checked against the *month's* length, not a flat 31. `Date.UTC`
  // normalises an overlong day silently, so a corrupt "2024:02:31" would
  // otherwise be reported as a confident 2 March rather than rejected — worse
  // than the pre-#1988 behaviour, which produced `Invalid Date` and moved on to
  // the next date field. The year's upper bound is what `toISOString` can
  // render in the four-digit form: past 9999 it emits the expanded `+275760-…`
  // spelling, which downstream ISO parsers reject.
  //
  // `seconds === 60` is left to roll into the following minute deliberately:
  // it is how a leap second is spelled, and normalising it costs one second
  // rather than a wrong date.
  const valid =
    parts.year >= 1900 &&
    parts.year <= 9999 &&
    parts.month >= 1 &&
    parts.month <= 12 &&
    parts.day >= 1 &&
    parts.day <= daysInMonth(parts.year, parts.month) &&
    parts.hours <= 23 &&
    parts.minutes <= 59 &&
    parts.seconds <= 60
  return valid ? parts : undefined
}

/** The wall clock reinterpreted as if the camera had been on UTC. */
const wallClockAsUtcMillis = (wall: WallClock): number =>
  Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hours,
    wall.minutes,
    wall.seconds,
  )

/**
 * Parse an EXIF 2.31 `OffsetTime*` tag ("+10:00", "-05:30") to minutes east of
 * UTC. Cameras that carry the tag without a value write spaces or NULs, so
 * anything that isn't a well-formed offset is treated as no offset at all.
 */
const parseExifOffsetTag = (value: unknown): number | undefined => {
  if (typeof value !== 'string') return undefined
  const match = value
    .replace(/\0/g, '')
    .trim()
    .match(/^([+-])(\d{2}):?(\d{2})$/)
  if (!match) return undefined
  const magnitude = Number(match[2]) * 60 + Number(match[3])
  const minutes = match[1] === '-' ? -magnitude : magnitude
  return isPlausibleUtcOffset(minutes) ? minutes : undefined
}

/**
 * The GPS receiver's clock as a UTC instant — by definition true UTC, and
 * usually the only absolute time reference in a drone image.
 *
 * Both halves are required. `GPSTimeStamp` on its own is a time of day with no
 * date to anchor it; guessing the date makes the derived offset ambiguous
 * either side of midnight, and a confidently wrong offset is worse than none.
 *
 * exifr revives `GPSTimeStamp` by joining the rational triple with colons
 * ("6:51:3"), so components are unpadded and the seconds may be fractional.
 */
const gpsUtcMillis = (
  dateStamp: unknown,
  timeStamp: unknown,
): number | undefined => {
  if (typeof dateStamp !== 'string' || typeof timeStamp !== 'string') {
    return undefined
  }
  const date = dateStamp
    .replace(/\0/g, '')
    .trim()
    .match(/^(\d{4})[-:](\d{2})[-:](\d{2})$/)
  if (!date) return undefined

  const time = timeStamp.replace(/\0/g, '').trim().split(':').map(Number)
  if (time.length < 3 || time.some((part) => !Number.isFinite(part))) {
    return undefined
  }
  const [hours, minutes, seconds] = time
  if (
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 60
  ) {
    return undefined
  }

  return Date.UTC(
    Number(date[1]),
    Number(date[2]) - 1,
    Number(date[3]),
    hours,
    minutes,
    Math.floor(seconds),
  )
}

/**
 * What the GPS receiver's clock establishes about a capture: the instant it
 * happened, and the offset the camera was keeping when it happened.
 */
interface GpsAnchor {
  /** The true UTC instant, straight from the receiver. */
  instantMillis: number
  /** The camera's UTC offset, snapped to {@link OFFSET_QUANTUM_MINUTES}. */
  offsetMinutes: number
}

/**
 * Anchor a capture against the GPS receiver's clock.
 *
 * The receiver's reading **is** the instant — it is UTC by construction — so
 * that is what gets returned, rather than a value re-derived from the camera's
 * wall clock. Re-deriving would put the camera's own clock error back into an
 * instant that had just been measured free of it: a camera drifting 37 seconds
 * yields a snapped offset that is right and an instant 37 seconds wrong,
 * labelled `'gps'` and treated downstream as authoritative.
 *
 * The offset is the difference between the two clocks, snapped to the
 * quarter-hour grid and then checked against the offsets that actually exist
 * ({@link isRealUtcOffset}). It is kept for display only.
 *
 * A snap that is not a real offset means the two clocks disagree by more than
 * drift — a camera set to the wrong year, or more than half a quantum out of
 * step — so the whole anchor is rejected rather than half-believed, and the
 * caller falls through to the weaker `'geo'` tier. Trusting the receiver's
 * instant while distrusting the pairing that produced it would be trusting a
 * reading we have just shown to be inconsistent with the file it came from.
 */
const deriveGpsAnchor = (
  wall: WallClock,
  gpsMillis: number | undefined,
): GpsAnchor | undefined => {
  if (gpsMillis === undefined) return undefined
  const rawMinutes = (wallClockAsUtcMillis(wall) - gpsMillis) / 60_000
  const snapped =
    Math.round(rawMinutes / OFFSET_QUANTUM_MINUTES) * OFFSET_QUANTUM_MINUTES
  if (!isRealUtcOffset(snapped)) return undefined
  return { instantMillis: gpsMillis, offsetMinutes: snapped }
}

/**
 * The UTC offset an IANA zone was on at a given instant, in minutes east.
 *
 * Uses `Intl` rather than a date library: Node ships full ICU, so this honours
 * the rules **in force at that instant** — a 2016 capture is resolved with
 * 2016's DST rules, not today's. `longOffset` formats as `GMT+11:00`, or a
 * bare `GMT` on zero.
 */
const zoneOffsetMinutesAt = (
  zone: string,
  instantMillis: number,
): number | undefined => {
  let label: string | undefined
  try {
    label = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(new Date(instantMillis))
      .find((part) => part.type === 'timeZoneName')?.value
  } catch {
    // An unknown zone name throws RangeError; treat it as no answer.
    return undefined
  }
  if (!label) return undefined
  if (label === 'GMT' || label === 'UTC') return 0

  const match = label.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/)
  if (!match) return undefined
  const magnitude = Number(match[2]) * 60 + Number(match[3] ?? 0)
  return match[1] === '-' ? -magnitude : magnitude
}

/**
 * The offset a zone was on when its clocks read {@link wall}.
 *
 * Two passes, because the answer depends on the instant and the instant depends
 * on the answer: read the offset at the wall clock treated as UTC, use it to
 * estimate the true instant, then re-read the offset there. The second reading
 * is what gets a capture near a DST transition onto the right side of it.
 *
 * The autumn repeated hour is genuinely ambiguous from a wall clock alone —
 * both offsets are defensible. This resolves to whichever was in force at the
 * first-pass estimate, deterministically, and the `'geo'` source marks the
 * result as the inference it is.
 */
const zoneOffsetForWallClock = (
  zone: string,
  wall: WallClock,
): number | undefined => {
  const asUtc = wallClockAsUtcMillis(wall)
  const firstPass = zoneOffsetMinutesAt(zone, asUtc)
  if (firstPass === undefined) return undefined
  const estimate = asUtc - firstPass * 60_000
  const offset = zoneOffsetMinutesAt(zone, estimate) ?? firstPass
  return isPlausibleUtcOffset(offset) ? offset : undefined
}

/** Apply a UTC offset to a wall clock, yielding a true instant. */
const wallClockToInstantIso = (
  wall: WallClock,
  offsetMinutes: number,
): string =>
  new Date(wallClockAsUtcMillis(wall) - offsetMinutes * 60_000).toISOString()

/**
 * Resolve the camera's offset from where it was standing.
 *
 * Returns `undefined` for every reason it might not work — no resolver
 * injected, no position in the file, a position outside the valid range, or a
 * zone the runtime does not know — so the caller falls through to
 * `'local-assumed'` rather than inventing an offset.
 */
const offsetFromPosition = (
  lookupTimeZone: ExtractMetadataOptions['lookupTimeZone'],
  position: PositionMetadata | undefined,
  wall: WallClock,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDebug?: (message: string, data?: Record<string, any>) => void,
): number | undefined => {
  if (!lookupTimeZone || !position) return undefined
  const { latitude, longitude } = position
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return undefined
  }

  let zone: string | undefined
  try {
    zone = lookupTimeZone(latitude, longitude)
  } catch {
    // A dataset miss throws in some implementations; that is a "don't know",
    // not a reason to fail the whole extraction.
    return undefined
  }
  if (!zone) return undefined

  const offset = zoneOffsetForWallClock(zone, wall)
  if (offset === undefined) {
    onDebug?.(`Could not resolve an offset for zone ${zone}`)
    return undefined
  }
  return offset
}

/**
 * EXIF date tags in the order we'd rather have them, each paired with the
 * offset tag that describes *that* tag's timezone.
 *
 * These are exifr's names, which are not EXIF's: tag 0x9004
 * (`DateTimeDigitized`) surfaces as `CreateDate`, and 0x0132 (`DateTime`)
 * surfaces as `ModifyDate`. The previous list asked for the EXIF spellings,
 * which exifr never emits — so `DateTimeOriginal` was in practice the only
 * date field that ever matched.
 */
const CAPTURE_DATE_FIELDS = [
  { date: 'DateTimeOriginal', offset: 'OffsetTimeOriginal' },
  { date: 'CreateDate', offset: 'OffsetTimeDigitized' },
  { date: 'ModifyDate', offset: 'OffsetTime' },
] as const

/**
 * Options for unified metadata extraction from EXIF data
 */
export interface ExtractMetadataOptions {
  /**
   * Function to parse EXIF data from the input source
   * Should return the result of exifr.parse()
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseExif: (options: { gps?: boolean; pick?: string[] }) => Promise<any>
  /**
   * Optional logger function for info/debug messages
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInfo?: (message: string, data?: Record<string, any>) => void
  /**
   * Optional logger function for debug messages
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDebug?: (message: string, data?: Record<string, any>) => void
  /**
   * Optional logger function for warnings
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onWarn?: (message: string, data?: Record<string, any>) => void
  /**
   * Resolve an IANA zone name from a GPS position, e.g. `'Australia/Sydney'`.
   *
   * Injected rather than imported so this module keeps no dependency on a
   * timezone dataset: the backend passes `tz-lookup`, and the browser upload
   * path — which only wants position and camera fields — passes nothing and
   * carries none of that weight into the bundle.
   *
   * Omitting it simply disables the `'geo'` tier; the two exact anchors and
   * the `'local-assumed'` fallback behave exactly as before.
   */
  lookupTimeZone?: (latitude: number, longitude: number) => string | undefined
}

/**
 * EXIF `GPSAltitude` is unsigned; `GPSAltitudeRef` 1 means below sea level.
 * exifr returns the ref as a byte array-like (`{ 0: 1 }` or `[1]`).
 */
function signedGpsAltitude(
  altitude: number | undefined,
  ref: unknown,
): number | undefined {
  if (altitude == null) return undefined
  const flag =
    typeof ref === 'object' && ref !== null ? (ref as { 0?: unknown })[0] : ref
  return flag === 1 ? -Math.abs(altitude) : altitude
}

function trimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.replace(/\0/g, '').trim()
  return trimmed || undefined
}

function pickDefined<T extends Record<string, unknown>>(values: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value != null),
  ) as Partial<T>
}

/**
 * Extract all available metadata (GPS and date/time) from image EXIF data in a single pass.
 * This function efficiently extracts both GPS coordinates and date/time information
 * using a single EXIF parse operation.
 *
 * @param options Configuration including the parse function and optional loggers
 * @returns Complete metadata if any is found, empty object otherwise
 */
export async function extractMetadataFromEXIF(
  options: ExtractMetadataOptions,
): Promise<ExifMetadata> {
  const { parseExif, onInfo, onDebug, onWarn, lookupTimeZone } = options

  try {
    onDebug?.('Attempting to extract metadata from image EXIF')

    // Extract all relevant EXIF fields in a single pass
    const exifData = await parseExif({
      pick: [
        // GPS fields
        'latitude',
        'longitude',
        'GPSAltitude',
        'GPSLatitude',
        'GPSLatitudeRef',
        'GPSLongitude',
        'GPSLongitudeRef',
        'GPSAltitudeRef',
        'RelativeAltitude',
        // Date/time fields (exifr's names — see CAPTURE_DATE_FIELDS)
        'DateTimeOriginal',
        'CreateDate',
        'ModifyDate',
        // Camera-stated UTC offsets (EXIF 2.31), the best capture-time anchor
        'OffsetTimeOriginal',
        'OffsetTimeDigitized',
        'OffsetTime',
        // The GPS receiver's UTC clock — the fallback anchor when the camera
        // states no offset of its own
        'GPSDateStamp',
        'GPSTimeStamp',
        // Camera fields
        'Make',
        'Model',
        'FocalLength',
        'FocalLengthIn35mmFormat',
        'CalibratedFocalLength',
        'ExifImageWidth',
        'ExifImageHeight',
        // Drone-specific fields
        'AbsoluteAltitude',
        'RelativeAltitude',
        'GimbalRollDegree',
        'GimbalYawDegree',
        'GimbalPitchDegree',
        'FlightRollDegree',
        'FlightYawDegree',
        'FlightPitchDegree',
        'FlightXSpeed',
        'FlightYSpeed',
        'FlightZSpeed',
      ],
    })

    const result: ExifMetadata = {}

    // Extract GPS position
    let position: PositionMetadata | undefined

    // First try the simplified approach (exifr auto-converts)
    if (exifData?.latitude != null && exifData?.longitude != null) {
      position = {
        latitude: exifData.latitude,
        longitude: exifData.longitude,
        absoluteAltitude: signedGpsAltitude(
          exifData.GPSAltitude,
          exifData.GPSAltitudeRef,
        ),
        relativeAltitude: exifData.RelativeAltitude,
      }
      onInfo?.('Successfully extracted GPS coordinates using simple method', {
        position,
      })
    } else if (
      exifData?.GPSLatitude != null &&
      exifData?.GPSLatitudeRef != null &&
      exifData?.GPSLongitude != null &&
      exifData?.GPSLongitudeRef != null
    ) {
      // Try extracting raw GPS tags
      const { convertDMSToDecimal } = await import('./geo-utils.js')

      const lat = convertDMSToDecimal(
        exifData.GPSLatitude,
        exifData.GPSLatitudeRef,
      )
      const lon = convertDMSToDecimal(
        exifData.GPSLongitude,
        exifData.GPSLongitudeRef,
      )

      const altitude = signedGpsAltitude(
        exifData.GPSAltitude,
        exifData.GPSAltitudeRef,
      )

      let relativeAltitude: number | undefined
      if (exifData.RelativeAltitude != null) {
        relativeAltitude = exifData.RelativeAltitude
      }

      position = {
        latitude: lat,
        longitude: lon,
        absoluteAltitude: altitude,
        relativeAltitude: relativeAltitude,
      }
      onInfo?.('Successfully extracted GPS coordinates using raw method', {
        position,
      })
    }

    if (position) {
      result.position = position
    } else {
      onDebug?.('No GPS data found in image EXIF')
    }

    // Extract the capture time, preferring the moment the shutter fired.
    //
    // An EXIF timestamp is the camera's local wall clock with no timezone
    // attached, so `toISOString()` on it mislabels the reading as UTC and
    // lands the instant hours out (droidmap#1988). Recover the offset from,
    // in order: the offset tag describing that very date tag, then the GPS
    // receiver's UTC clock. Only when neither exists do we stamp the wall
    // clock as UTC — and then `timestampSource` says so, so no reader has to
    // guess whether it holds an instant or a clock face.
    for (const {
      date: dateField,
      offset: offsetField,
    } of CAPTURE_DATE_FIELDS) {
      const dateValue = exifData?.[dateField]
      if (!dateValue) continue

      const wallClock = readWallClock(dateValue)
      if (!wallClock) {
        onDebug?.(`Unusable date value for ${dateField}: ${String(dateValue)}`)
        continue
      }

      const statedOffset = parseExifOffsetTag(exifData?.[offsetField])
      const gpsAnchor =
        statedOffset === undefined
          ? deriveGpsAnchor(
              wallClock,
              gpsUtcMillis(exifData?.GPSDateStamp, exifData?.GPSTimeStamp),
            )
          : undefined
      // Last resort before giving up: the coordinates say where the camera was,
      // and where it was says what its clock meant (droidmap#2783). Only
      // consulted when neither exact anchor exists, so an inference can never
      // displace an observation — including when the GPS clock was consulted
      // and rejected, which is a "don't know" and not an answer.
      const geoOffset =
        statedOffset === undefined && gpsAnchor === undefined
          ? offsetFromPosition(
              lookupTimeZone,
              result.position,
              wallClock,
              onDebug,
            )
          : undefined

      const offsetMinutes =
        statedOffset ?? gpsAnchor?.offsetMinutes ?? geoOffset

      const source: CaptureTimestampSource =
        statedOffset !== undefined
          ? 'exif-offset'
          : gpsAnchor !== undefined
            ? 'gps'
            : geoOffset !== undefined
              ? 'geo'
              : 'local-assumed'

      // The GPS receiver reported the instant itself, so use it rather than
      // rebuilding one from the camera's wall clock and the offset derived
      // from it — that round trip reintroduces the camera's clock error.
      // Everywhere else the wall clock is all there is, and `?? 0` is the
      // legacy behaviour, deliberately: with no offset to apply the best we
      // can do is the previous convention, and `source` marks it as the guess
      // it is rather than letting it pass for an instant.
      result.timestamp =
        gpsAnchor !== undefined
          ? new Date(gpsAnchor.instantMillis).toISOString()
          : wallClockToInstantIso(wallClock, offsetMinutes ?? 0)
      result.timestampSource = source
      if (offsetMinutes !== undefined) {
        result.timestampOffsetMinutes = offsetMinutes
      }

      onInfo?.(`Successfully extracted date/time using field: ${dateField}`, {
        field: dateField,
        timestamp: result.timestamp,
        timestampSource: source,
        timestampOffsetMinutes: offsetMinutes,
      })
      break // Use the first usable date found
    }

    if (!result.timestamp) {
      onDebug?.('No date/time data found in image EXIF')
    }

    // Extract camera settings. Checked with `!= null` rather than truthiness:
    // a gimbal roll or pitch of exactly 0 is a common, meaningful reading.
    const cameraSettings: CameraMetadata = pickDefined({
      make: trimmedString(exifData.Make),
      model: trimmedString(exifData.Model),
      focalLength: exifData.FocalLength,
      focalLengthIn35mm: exifData.FocalLengthIn35mmFormat,
      calibratedFocalLength: exifData.CalibratedFocalLength,
      exifImageWidth: exifData.ExifImageWidth,
      exifImageHeight: exifData.ExifImageHeight,
    })

    if (Object.keys(cameraSettings).length > 0) {
      result.camera = cameraSettings
      onInfo?.('Successfully extracted camera settings', {
        settings: cameraSettings,
      })
    }

    // Extract drone-specific metadata
    const droneMetadata: DroneMetadata = pickDefined({
      gimbalRollDegree: exifData.GimbalRollDegree,
      gimbalYawDegree: exifData.GimbalYawDegree,
      gimbalPitchDegree: exifData.GimbalPitchDegree,
      flightRollDegree: exifData.FlightRollDegree,
      flightYawDegree: exifData.FlightYawDegree,
      flightPitchDegree: exifData.FlightPitchDegree,
      flightXSpeed: exifData.FlightXSpeed,
      flightYSpeed: exifData.FlightYSpeed,
      flightZSpeed: exifData.FlightZSpeed,
    })

    if (Object.keys(droneMetadata).length > 0) {
      result.drone = droneMetadata
      onInfo?.('Successfully extracted drone metadata', {
        metadata: droneMetadata,
      })
    }

    return cleanEmpty(result)
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    onWarn?.('Failed to extract metadata from image', {
      error: errorMessage,
    })
    // Don't throw - metadata extraction is optional
    return {}
  }
}

/**
 * Check if a filename matches macOS metadata file patterns.
 *
 * macOS creates metadata files alongside real files:
 * - `.DS_Store` - Directory metadata files
 * - `._*` - AppleDouble files containing resource forks and extended attributes
 *
 * These files are not actual image/asset files and cause issues with
 * image dimension calculation and job size estimation.
 *
 * @param filename - The filename to check
 * @returns true if the file is a macOS metadata file, false otherwise
 *
 * @example
 * ```typescript
 * isMacOSMetadataFile('.DS_Store') // true
 * isMacOSMetadataFile('._image.jpg') // true
 * isMacOSMetadataFile('image.jpg') // false
 * isMacOSMetadataFile('my._file.jpg') // false
 * ```
 */
export function isMacOSMetadataFile(
  filename: string | undefined | null,
): boolean {
  // If filename is undefined or null, it's not a macOS metadata file
  if (!filename) {
    return false
  }
  // Filter out .DS_Store files (case-insensitive)
  if (filename.toLowerCase() === '.ds_store') {
    return true
  }
  // Filter out AppleDouble files (start with ._ )
  if (filename.startsWith('._')) {
    return true
  }
  return false
}
